import React, { useState } from "react";
import { Card, Row, Col, Table, Statistic, Tag, Typography } from "antd";
import { ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import { ComboConfig, FormColumnConfig, MasterConfigs } from "../../../../interfaces/entity";
import MasterComponent from "../../../../components/Forms";
import { constants } from "../../../../constants/common";
import dayjs from "dayjs";
import moment from "moment";
import SmallProfileCard from "../../../../components/ContactInfo/SmallProfileCard";
import { formatPriceIndian } from "../../../../constants/functions";

const { Title } = Typography;

const SetBDETargets = () => {
  const [fiscalYear, setFiscalYear] = useState(null);
  const [targetType, setTargetType] = useState(null);
  const config: MasterConfigs = {
    api: () => `/bde-targets`,
    queryParams: {},
    isActionTab: true,
    getColumns: () => [
      {
        title: "BDE Name", dataIndex: "bdeName", key: "bdeName", render: (text) => <SmallProfileCard
          name={constants.GET_FULL_NAME(text)}
          avatar={text?.avatar}
          subTitle={text?.employeeId}
        />
      },
      {
        title: "Target Type", dataIndex: "targetType", key: "targetType", render: (text) => constants.TARGET_TYPES.find(type => type.value === text)?.name || text
      },
      { title: "Target (₹)", dataIndex: "targetValue", key: "targetValue", render: (text) => text ? `₹ ${formatPriceIndian(text)}` : '-' },
      { title: "Deals Needed", dataIndex: "dealsNeeded", key: "dealsNeeded" },
      { title: "Active Accounts", dataIndex: "activeAccounts", key: "activeAccounts" },
      { title: "Leads Needed", dataIndex: "leadsNeeded", key: "leadsNeeded" },
      {
        title: "Fiscal Year", dataIndex: "fiscalYear", key: "fiscalYear",
        render: (text) => text?.name
      },
      {
        title: "Quarter", dataIndex: "quarter", key: "quarter", render: (text) => {
          const start = moment(text?.startDate);
          const end = moment(text?.endDate);
          return (
            <span>
              {text?.name} - <span className="text-gray-500 text-sm">{start.format("MMM D")} - {end.format("MMM D")}</span>
              {moment().isBetween(start, end) && <Tag color="green" bordered={false}>Current</Tag>}
            </span>
          );
        }
      },
      { title: "Target Type", dataIndex: "targetType", key: "targetType", render: (text) => constants.TARGET_TYPES.find(type => type.value === text)?.name || text },
      { title: "Created At", dataIndex: "createdAt", key: "createdAt", render: (text) => dayjs(text).format("MMM D, YYYY") }
    ],
    onDataLoad: (data) => {
      if (data) {
        setFiscalYear(data.fiscalYear);
        setTargetType(data.targetType);
      }
    },
    getFormColumns: () => [
      {
        label: "BDE Name",
        name: "bdeName",
        type: 'select',
        isCombo: true,
        rules: [{ required: true, message: "Please select a BDE!" }],
      },
      {
        label: "Fiscal Year",
        name: "fiscalYear",
        type: 'select',
        isCombo: true,
        rules: [{ required: true, message: "Please select the fiscal year!" }],
        selectProps: {
          showSearch: true,
          allowClear: true,
          onChange: (value) => {
            setFiscalYear(value);
          }
        }
      },
      {
        label: "Quarter",
        name: "quarter",
        type: 'select',
        isCombo: true,
        selectProps: {
          showSearch: true,
          allowClear: true,
          disabled: !fiscalYear,
        },
        rules: [{ required: true, message: "Please select the quarter!" }],
      },
      {
        label: "Target Type",
        name: "targetType",
        type: 'select',
        children: constants.TARGET_TYPES,
        rules: [{ required: true, message: "Please select the target type!" }],
        selectProps: {
          showSearch: true,
          allowClear: true,
          onChange: (value) => {
            setTargetType(value);
          }
        }
      },
      ['revenue'].includes(targetType) && {
        label: "Target (₹)",
        name: "targetValue",
        type: 'number',
        rules: [{ required: true, message: "Please input the target amount!" }],
      },
      ['revenue', 'deals'].includes(targetType) && {
        label: "Deals need's to win",
        name: "dealsNeeded",
        type: 'number',
        rules: [{ required: true, message: "Please input the deals need's to win!" }],
      },
      ['revenue', 'accounts'].includes(targetType) && {
        label: "Active Accounts",
        name: "activeAccounts",
        type: 'number',
        rules: [{ required: true, message: "Please input the active accounts!" }],
      },
      ['revenue', 'leads'].includes(targetType) && {
        label: "Leads need's to generate",
        name: "leadsNeeded",
        type: 'number',
        rules: [{ required: true, message: "Please input the leads need's to generate!" }],
      },
      { span: 8 },
      {
        type: 'custom',
        formItem: () => <Title level={5} className="text-gray-500">Daily/Weekly/Monthly Targets</Title>,
        span: 24
      },
      {
        name: "dailyTargets",
        type: 'form',
        span: 24,
        form: [
          {
            label: "Target Period",
            name: "period",
            type: 'select',
            children: [{ name: 'Monthly', value: 'monthly' }, { name: 'Weekly', value: 'weekly' }, { name: 'Daily', value: 'daily' }],
            span: 8,
            rules: [{ required: true, message: "Please select a period!" }],
          },
          {
            label: "Target For",
            name: "targetFor",
            type: 'select',
            children: constants.BDE_TARGET_TYPES,
            span: 8,
            rules: [{ required: true, message: "Please select a target type!" }],
          },
          {
            label: "Target Value",
            name: "targetValue",
            type: 'number',
            span: 8,
            rules: [{ required: true, message: "Please enter the target value!" }],
          }
        ]
      },
      {
        label: "Description",
        name: "description",
        type: 'textarea',
        span: 16,
      }
    ].filter(Boolean) as FormColumnConfig[],
    combo: [
      {
        api: '/users',
        idKey: '_id',
        render: (text) => constants.GET_FULL_NAME(text),
        name: 'bdeName'
      },
      fiscalYear && {
        api: `/quarters?fiscalYear=${fiscalYear}`,
        idKey: '_id',
        render: (quarter) => {
          const today = moment();
          const start = moment(quarter.startDate);
          const end = moment(quarter.endDate);

          let statusTag;
          if (today.isBetween(start, end, null, '[]')) {
            statusTag = <Tag color="green" bordered={false}>Current</Tag>;
          } else if (today.isAfter(end)) {
            statusTag = <Tag color="blue" bordered={false}>Passed</Tag>;
          } else {
            statusTag = <Tag color="orange" bordered={false}>Upcoming</Tag>;
          }

          return (
            <span>
              {quarter.name} - <span className="text-gray-500 text-sm">{start.format("MMM D")} - {end.format("MMM D")}</span> - {statusTag}
            </span>
          );
        },
        name: 'quarter',
        rerender: [fiscalYear]
      },
      {
        api: `/fiscal-year`,
        idKey: '_id',
        render: (text) => text.name,
        name: 'fiscalYear'
      }
    ].filter(Boolean) as ComboConfig[],
    actions: [
      {
        actionType: "Add",
        name: "Add Target",
      }
    ]
  }

  return (
    <MasterComponent config={config} />
  );
};

export default SetBDETargets;